import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Navbar, Footer, ScrollToTop } from "@/components/shared";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import ProgramsPage from "@/pages/ProgramsPage";
import ContactPage from "@/pages/ContactPage";
import WebinarPage from "@/pages/WebinarPage";
import EventsPage from "@/pages/EventsPage";
import AICatalystPage from "@/pages/AICatalystPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AICoEPage from "@/pages/AICoEPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import FooterPreviewPage from "@/pages/FooterPreviewPage";

// Footer Padding Diagram (debug utility — kept inline as it's rarely used)
const FooterDiagram = () => (
  <div className="min-h-screen bg-slate-900 text-white p-8 pt-24">
    <h1 className="text-3xl font-bold text-center mb-8">Footer Padding Diagram</h1>
    <p className="text-center text-slate-400 mb-12">Visual representation of current footer spacing</p>
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-wrap gap-6 justify-center mb-8">
        <div className="flex items-center gap-2"><div className="w-6 h-6 bg-red-500/30 border-2 border-red-500"></div><span className="text-sm">margin-top (mt-8 = 32px)</span></div>
        <div className="flex items-center gap-2"><div className="w-6 h-6 bg-blue-500/30 border-2 border-blue-500"></div><span className="text-sm">padding-top (pt-16 = 64px)</span></div>
        <div className="flex items-center gap-2"><div className="w-6 h-6 bg-green-500/30 border-2 border-green-500"></div><span className="text-sm">padding-bottom (pb-12 = 48px)</span></div>
      </div>
      <div className="border-2 border-slate-600 rounded-lg overflow-hidden">
        <div className="bg-[#121E30] p-8 text-center border-b-0">
          <p className="text-slate-400 text-sm mb-2">Last Section (CTA Section)</p>
          <h3 className="text-xl font-bold">Ready to Transform?</h3>
        </div>
        <div className="relative"><div className="h-1 bg-[#2A3648] relative"><div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-slate-800 px-3 py-1 rounded text-xs text-amber-400 whitespace-nowrap">border-t border-[#2A3648] (1px)</div></div></div>
        <div className="bg-[#080F1A]">
          <div className="h-8 bg-red-500/20 border-l-4 border-r-4 border-red-500 flex items-center justify-center"><span className="text-xs text-red-400 font-mono">mt-8 = 32px</span></div>
          <div className="h-16 bg-blue-500/20 border-l-4 border-r-4 border-blue-500 flex items-center justify-center"><span className="text-xs text-blue-400 font-mono">pt-16 = 64px</span></div>
          <div className="bg-[#080F1A] border-2 border-dashed border-slate-500 mx-4 p-6">
            <p className="text-center text-slate-300 text-sm mb-4">Footer Content Area</p>
            <div className="grid grid-cols-4 gap-4 text-xs text-slate-500">
              <div className="bg-slate-800 p-3 rounded text-center"><p className="font-bold text-slate-300">Brand</p></div>
              <div className="bg-slate-800 p-3 rounded text-center"><p className="font-bold text-slate-300">Quick Links</p></div>
              <div className="bg-slate-800 p-3 rounded text-center"><p className="font-bold text-slate-300">Programs</p></div>
              <div className="bg-slate-800 p-3 rounded text-center"><p className="font-bold text-slate-300">Contact</p></div>
            </div>
          </div>
          <div className="h-12 bg-green-500/20 border-l-4 border-r-4 border-green-500 flex items-center justify-center"><span className="text-xs text-green-400 font-mono">pb-12 = 48px</span></div>
        </div>
      </div>
      <div className="mt-8 text-center">
        <Link to="/" className="text-[#C9A962] hover:underline">&larr; Back to Home</Link>
      </div>
    </div>
  </div>
);

// Layout wrapper — hides Navbar/Footer on standalone pages
const MainLayout = ({ children }) => {
  const location = useLocation();
  const isStandalone = location.pathname.startsWith('/webinar/') || location.pathname === '/footer-diagram';
  
  return (
    <div className="min-h-screen bg-[#0A1628]">
      {!isStandalone && <Navbar />}
      <main>{children}</main>
      {!isStandalone && <Footer />}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <MainLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/programs" element={<ProgramsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/ai-catalyst" element={<AICatalystPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/ai-centre-of-excellence" element={<AICoEPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/webinar/intel-unnati" element={<WebinarPage />} />
          <Route path="/footer-preview" element={<FooterPreviewPage />} />
          <Route path="/footer-diagram" element={<FooterDiagram />} />
        </Routes>
      </MainLayout>
    </BrowserRouter>
  );
}

export default App;
